import type { FC } from 'react'

import { Icon } from '@skytech/ui'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import CheckCircleIcon from 'mdi-react/CheckCircleIcon'
import ImageOffOutlineIcon from 'mdi-react/ImageOffOutlineIcon'
import LockIcon from 'mdi-react/LockIcon'
import { twJoin, twMerge } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import { selectedStyleByWhiteLabel } from '@/components/util/style-constant'

export const PolicyPictures: FC<{
  images: string[]
  isSelected?: boolean
  isDisabled?: boolean
  className?: string
}> = ({ images, isSelected = false, isDisabled = false, className }) => {
  const { whiteLabel } = useAppContext()

  return (
    <div
      className={twMerge(
        'small-mobile:grid small-mobile:grid-cols-[repeat(2,_3rem)] small-mobile:grid-rows-[repeat(2,3rem)] relative m-2 hidden justify-center rounded-2xl bg-white md:mr-3 md:flex md:h-16 md:items-center',
        (isDisabled || isSelected) && 'md:min-w-16',
        className
      )}
    >
      {images.map((imageUrl, idx) => (
        <div
          key={imageUrl || idx}
          className={twMerge(
            'md:shadow-basic-black flex items-center justify-center p-2 md:-ml-4 md:h-12 md:w-12 md:overflow-hidden md:rounded-full md:border-none md:bg-white md:first:ml-0',
            'first:border first:border-solid first:border-b-gray-200 first:border-l-transparent first:border-r-gray-200 first:border-t-transparent first:md:border-none',
            '[&:nth-of-type(2)]:border [&:nth-of-type(2)]:border-solid [&:nth-of-type(2)]:border-x-transparent [&:nth-of-type(2)]:border-b-gray-200 [&:nth-of-type(2)]:border-t-transparent [&:nth-of-type(2)]:md:border-none',
            '[&:nth-of-type(3)]:border [&:nth-of-type(3)]:border-solid [&:nth-of-type(3)]:border-y-transparent [&:nth-of-type(3)]:border-l-transparent [&:nth-of-type(3)]:border-r-gray-200 [&:nth-of-type(3)]:md:border-none',
            isDisabled && 'opacity-70',
            isSelected && 'opacity-50'
          )}
        >
          <ImageWithIconFallback
            src={imageUrl}
            alt="Policy exemplary device"
            className={twJoin('max-h-full max-w-full')}
            fallbackIcon={<ImageOffOutlineIcon />}
          />
        </div>
      ))}
      {(isSelected || isDisabled) && (
        <div className="absolute flex w-full items-center justify-center opacity-100 max-md:h-full">
          {isDisabled && (
            <Icon className="rounded-full">
              <LockIcon
                className={twJoin(
                  'm-auto block size-16 w-full rounded-full p-2 opacity-100',
                  selectedStyleByWhiteLabel[whiteLabel]?.fill ||
                    selectedStyleByWhiteLabel.skytech!.fill,
                  selectedStyleByWhiteLabel[whiteLabel]?.text ||
                    selectedStyleByWhiteLabel.skytech!.text
                )}
              />
            </Icon>
          )}
          {isSelected && (
            <Icon
              className={twJoin(
                'm-2 h-14 items-center rounded-full',
                images.length > 1 ? 'p-2' : 'px-0 py-2'
              )}
            >
              <CheckCircleIcon
                className={twJoin(
                  'block size-full rounded-full fill-white text-white opacity-100',
                  selectedStyleByWhiteLabel[whiteLabel]?.bg || selectedStyleByWhiteLabel.skytech!.bg
                )}
              />
            </Icon>
          )}
        </div>
      )}
    </div>
  )
}
